import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  getMyOnboardingState,
  markOnboardingStep,
  getMyGhlAccount,
} from "@/lib/ghl-connect.functions";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CheckCircle2, Circle, ExternalLink, Info } from "lucide-react";

export const Route = createFileRoute("/admin/social/setup-guide")({
  component: GuidePage,
  ssr: false,
  head: () => ({ meta: [{ title: "Setup guide · Social" }] }),
});

function StripeBuyButton() {
  return React.createElement("stripe-buy-button", {
    "buy-button-id": "buy_btn_1TbiKdIxHTrEKAusWVzByWRP",
    "publishable-key": "pk_live_Kv1rNHtNJZizqKjTUlrteVoS00yZ0vTMNK",
  });
}

function useStripeBuyButtonScript() {
  useEffect(() => {
    const id = "stripe-buy-button-js";
    if (document.getElementById(id)) return;
    const s = document.createElement("script");
    s.id = id;
    s.async = true;
    s.src = "https://js.stripe.com/v3/buy-button.js";
    document.head.appendChild(s);
  }, []);
}

function StepCard({
  n,
  title,
  done,
  children,
}: {
  n: number;
  title: string;
  done: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-border bg-card p-5">
      <header className="mb-3 flex items-center gap-3">
        <div
          className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${
            done
              ? "bg-emerald-500 text-white"
              : "bg-primary/10 text-primary"
          }`}
        >
          {done ? <CheckCircle2 className="h-4 w-4" /> : n}
        </div>
        <h2 className="text-lg font-semibold">{title}</h2>
        {done && <span className="ml-auto text-xs text-emerald-600">Done</span>}
      </header>
      <div className="space-y-3 text-sm text-muted-foreground [&_strong]:text-foreground [&_a]:text-primary [&_a]:underline">
        {children}
      </div>
    </section>
  );
}

function GuidePage() {
  useStripeBuyButtonScript();
  const stateFn = useServerFn(getMyOnboardingState);
  const acctFn = useServerFn(getMyGhlAccount);
  const markFn = useServerFn(markOnboardingStep);

  const state = useQuery({ queryKey: ["onb-state"], queryFn: () => stateFn() });
  const acct = useQuery({ queryKey: ["my-ghl-account"], queryFn: () => acctFn() });

  const s = state.data?.state;
  const step6Done = !!acct.data?.account?.last_test_ok;

  const toggle = async (
    step:
      | "step1_understood"
      | "step2_subscribed"
      | "step3_ghl_provisioned"
      | "step4_signed_in_ghl"
      | "step5_socials_connected"
      | "step7_content_options_set",
    value: boolean,
  ) => {
    await markFn({ data: { step, value } });
    state.refetch();
  };

  return (
    <div className="max-w-3xl space-y-5">
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-5">
        <h1 className="font-display text-2xl font-bold">Social media setup guide</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Follow these 7 steps to get your social posts publishing automatically through your own
          GHL sub-account.
        </p>
      </div>

      <StepCard n={1} title="How it works" done={!!s?.step1_understood}>
        <p>
          We provision a private GoHighLevel (GHL) sub-account for you. You connect your social
          profiles (Facebook, Instagram, LinkedIn, Google, TikTok, X) inside GHL, then link that
          sub-account back here. Our AI planner, evergreen library, and compliance tools then post
          on your behalf — in your brand voice, with your logo, with your disclaimer attached.
        </p>
        <Button size="sm" variant={s?.step1_understood ? "outline" : "default"} onClick={() => toggle("step1_understood", !s?.step1_understood)}>
          {s?.step1_understood ? "Mark as not done" : "Got it"}
        </Button>
      </StepCard>

      <StepCard n={2} title="Sign up — $9.99 / month" done={!!s?.step2_subscribed}>
        <p>This covers your GHL sub-account, hosting, and all the AI features on this dashboard.</p>
        <div className="my-2">
          <StripeBuyButton />
        </div>
        <p className="text-xs">
          Button not loading? Use the direct secure checkout link instead:
        </p>
        <Button asChild size="sm" className="!text-primary-foreground !no-underline">
          <a
            href="https://buy.stripe.com/00wcN6dRS0Ny9Vw6mweUU0d"
            target="_blank"
            rel="noreferrer"
          >
            Sign up — $9.99 / month <ExternalLink className="ml-1.5 inline h-3.5 w-3.5" />
          </a>
        </Button>
        <div className="pt-2">
          <Button size="sm" variant={s?.step2_subscribed ? "outline" : "default"} onClick={() => toggle("step2_subscribed", !s?.step2_subscribed)}>
            {s?.step2_subscribed ? "Mark as not subscribed" : "I subscribed"}
          </Button>
        </div>
      </StepCard>

      <StepCard n={3} title="Wait for us to set up your GHL account" done={!!s?.step3_ghl_provisioned}>
        <p>
          We'll email your login credentials within <strong>1 business day</strong> of your
          subscription. If you haven't received them after 24 hours, email{" "}
          <a href="mailto:support@simplyapprovedbusinessloans.com">
            support@simplyapprovedbusinessloans.com
          </a>
          .
        </p>
        <Button size="sm" variant={s?.step3_ghl_provisioned ? "outline" : "default"} onClick={() => toggle("step3_ghl_provisioned", !s?.step3_ghl_provisioned)}>
          {s?.step3_ghl_provisioned ? "Mark as not received" : "I received my login"}
        </Button>
      </StepCard>

      <StepCard n={4} title="Sign in to GoHighLevel" done={!!s?.step4_signed_in_ghl}>
        <p>
          Use the credentials we emailed you to log in at{" "}
          <a href="https://app.gohighlevel.com" target="_blank" rel="noreferrer">
            app.gohighlevel.com <ExternalLink className="inline h-3 w-3" />
          </a>
          . You'll be asked to set up 2-factor authentication on first login — please do this; it
          protects your social accounts.
        </p>
        <Button size="sm" variant={s?.step4_signed_in_ghl ? "outline" : "default"} onClick={() => toggle("step4_signed_in_ghl", !s?.step4_signed_in_ghl)}>
          {s?.step4_signed_in_ghl ? "Mark as not signed in" : "I'm signed in"}
        </Button>
      </StepCard>

      <StepCard n={5} title="Connect your social accounts inside GHL" done={!!s?.step5_socials_connected}>
        <p>Inside your GHL account:</p>
        <ol className="ml-5 list-decimal space-y-1">
          <li>Open <strong>Marketing → Social Planner</strong>.</li>
          <li>Click <strong>Connect Accounts</strong> (top-right).</li>
          <li>
            Pick each platform — Facebook Page, Instagram Business, LinkedIn Company Page, Google
            Business Profile, TikTok Business, X (Twitter) — and authorize. Repeat for each profile
            you want to publish to.
          </li>
        </ol>
        <p>
          Tip: for Instagram, your Instagram must be a Business account linked to a Facebook Page.
        </p>
        <Alert className="mt-3 border-amber-500/30 bg-amber-500/5">
          <Info className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-700">TikTok domain verification required</AlertTitle>
          <AlertDescription className="text-amber-700/80">
            <p>
              Before TikTok will accept video uploads from this platform, your domain must be
              verified in TikTok Developer Portal.
            </p>
            <p className="mt-2 font-medium">Add this DNS TXT record at your domain registrar:</p>
            <ul className="mt-1 ml-5 list-disc space-y-0.5 text-xs">
              <li><strong>Type:</strong> TXT</li>
              <li><strong>Name:</strong> @ (root domain)</li>
              <li><strong>Value:</strong> tiktok-developers-site-verification=FnoER38udyUzxZVptH8y2qWd5iNVLPLt</li>
            </ul>
            <p className="mt-2 text-xs">
              Wait for DNS propagation, then click <em>Verify</em> in the TikTok Developer Portal
              under <strong>Settings → Domains</strong>. If your domain is managed in Lovable, go to
              Project Settings → Domains → Configure → Manage DNS records.
            </p>
          </AlertDescription>
        </Alert>
        <Button size="sm" variant={s?.step5_socials_connected ? "outline" : "default"} onClick={() => toggle("step5_socials_connected", !s?.step5_socials_connected)}>
          {s?.step5_socials_connected ? "Mark as not connected" : "Socials connected in GHL"}
        </Button>
      </StepCard>

      <StepCard n={6} title="Connect GHL to this platform" done={step6Done}>
        <p>Inside GHL, copy two things and paste them on our Connect page:</p>
        <ol className="ml-5 list-decimal space-y-1">
          <li>
            <strong>Location ID</strong>: GHL → Settings → Business Profile → "Location ID".
          </li>
          <li>
            <strong>Private Integration Token (PIT)</strong>: GHL → Settings → Private Integrations
            → <em>Create new</em>.
          </li>
        </ol>
        <p className="font-medium text-foreground">
          When creating the PIT, enable all of these Social Planner scopes:
        </p>
        <ul className="ml-5 list-disc space-y-1">
          <li><code>socialplanner/oauth.readonly</code> — View Social Media Integrations</li>
          <li><code>socialplanner/oauth.write</code> — Edit Social Media Integrations</li>
          <li><code>socialplanner/post.readonly</code> — View Social Media Posts</li>
          <li><code>socialplanner/post.write</code> — Edit Social Media Posts</li>
          <li><code>socialplanner/account.readonly</code> — View Social Media Accounts</li>
          <li><code>socialplanner/account.write</code> — Edit Social Media Accounts</li>
          <li><code>socialplanner/csv.readonly</code> — View Social Media CSV</li>
          <li><code>socialplanner/csv.write</code> — Edit Social Media CSV</li>
          <li><code>socialplanner/category.readonly</code> — View Social Media Categories</li>
          <li><code>socialplanner/category.write</code> — Edit Social Media Categories</li>
          <li><code>socialplanner/statistics.readonly</code> — View Social Media Statistics</li>
          <li><code>socialplanner/tag.readonly</code> — View Social Media Tags</li>
          <li><code>socialplanner/tag.write</code> — Edit Social Media Tags</li>
          <li><code>medias.readonly</code> — View Media Library <strong>(required for TikTok)</strong></li>
          <li><code>medias.write</code> — Edit Media Library <strong>(required for TikTok)</strong></li>
        </ul>
        <p className="text-xs">
          <strong>TikTok note:</strong> TikTok only accepts videos hosted on GoHighLevel's own
          (verified) media domain — it will reject a video served from any other domain. So when you
          post a TikTok, we first upload your video into your GoHighLevel media library (exactly what
          happens when you upload manually in GHL) and post that URL. This needs the two
          <code>medias</code> scopes above. Without them TikTok posts fail with a “not authorized for
          this scope” error.
        </p>
        <p className="text-xs">
          These cover everything (it's fine — and recommended — to grant all of them). Compose &amp;
          CSV need the write scopes; Calendar, Accounts and Analytics need the readonly scopes; the
          two <code>medias</code> scopes are required for TikTok video posting.
        </p>
        <Button asChild size="sm" className="!text-primary-foreground !no-underline">
          <Link to="/admin/social/connect">Open Connect GHL form</Link>
        </Button>
      </StepCard>

      <StepCard n={7} title="Set your content options" done={!!s?.step7_content_options_set}>
        <ul className="ml-5 list-disc space-y-1">
          <li>
            <Link to="/admin/social/settings">Settings</Link> — pick frequency (daily, weekly, or
            monthly) and topic mix.
          </li>
          <li>
            <Link to="/admin/social/compliance">Compliance</Link> — set brand tone, colors, logo,
            and the disclaimer that auto-appends to every AI post.
          </li>
          <li>
            <Link to="/admin/social/evergreen">Evergreen</Link> — your always-on library that recycles
            on a schedule.
          </li>
        </ul>
        <Button size="sm" variant={s?.step7_content_options_set ? "outline" : "default"} onClick={() => toggle("step7_content_options_set", !s?.step7_content_options_set)}>
          {s?.step7_content_options_set ? "Mark as not done" : "Options set"}
        </Button>
      </StepCard>
    </div>
  );
}
